import React, { useEffect, useMemo, useState } from 'react';
import { FileJson, Save, RotateCcw, Search, ShieldCheck, AlertCircle } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { Badge } from '../../../../components/ui/Badge';
import { Input } from '../../../../components/ui/Input';
import { loadGovernanceConfig, saveGovernanceConfig, type GovernanceConfigOverrides } from '../../../../governance';
import { hasDataSchema, getUnregisteredDataSchemaAllowlist } from '../../../../content';
import type { QuestionType } from '../../../../content/types';
/**
 * SchemaEnforcementSection — FG4 §Fase C UI (S9 Deelblok B).
 *
 * Per-question-type strict-schema enforcement ramp. Mirror of
 * EnforcementSection but for `enforcement.schemas` instead of
 * `enforcement.pedagogical`. Lets an operator walk a QuestionType along
 * `off → shadow → warn → enforce` once schemaStage starts consuming the
 * mode (today the resolver exists and unconfigured = 'enforce').
 *
 * Surfaces the S9 introspection signal alongside each type:
 *   • REGISTERED — DATA_VALIDATORS has a concrete validator
 *   • ALLOWLIST  — explicit named exemption (sub-validator elsewhere or no
 *                  data payload)
 *   • UNREGISTERED — neither; validateQuestionData fails with
 *                    UNREGISTERED_DATA_SCHEMA (anti-bandaid #3: visible debt)
 *
 * Owns its own load/save lifecycle so the wider GovernanceSettingsTab
 * reducer stays untouched (same pattern as EnforcementSection).
 */
type Mode = 'off' | 'shadow' | 'warn' | 'enforce';
const MODE_META: Record<Mode, {
  label: string;
  description: string;
  pill: string;
}> = {
  off: {
    label: 'Off',
    description: 'Niet evalueren. Schema-issues worden stilletjes gedropt.',
    pill: 'bg-slate-100 text-slate-700 border-slate-200'
  },
  shadow: {
    label: 'Shadow',
    description: 'Evalueren, "would block" loggen in Shadow Impact Report, NIET blokkeren.',
    pill: 'bg-violet-100 text-violet-700 border-violet-200'
  },
  warn: {
    label: 'Warn',
    description: 'Evalueren, blockers degraderen naar coach (zichtbaar, niet blokkerend).',
    pill: 'bg-amber-100 text-amber-700 border-amber-200'
  },
  enforce: {
    label: 'Enforce',
    description: 'Evalueren en blokkeren bij faal (volledige strictheid).',
    pill: 'bg-red-100 text-red-700 border-red-200'
  }
};
const MODES: Mode[] = ['off', 'shadow', 'warn', 'enforce'];
// All QuestionTypes the admin can configure. Keep alphabetised by category
// for predictable UI order (mirrors src/content/types.ts grouping).
const ALL_QUESTION_TYPES: QuestionType[] = ['multiple_choice', 'true_false_series', 'fill_in', 'drag_drop_fill', 'matching', 'ordering', 'categorize', 'error_spotting', 'text_highlight', 'sentence_parser', 'sentence_error_judge', 'verb_conjugation_flow', 'comparison_judge', 'estimation_slider', 'thermometer_slider', 'number_sequence', 'ratio_table', 'table_reading', 'schedule_reading', 'bar_chart', 'line_chart', 'pie_chart', 'cijferend_rekenen', 'cijferend_delen', 'clock', 'clock_set', 'money', 'multiplication_grid', 'grid_shade', 'step_solver', 'strategy_solver', 'square_root_builder', 'place_value_build', 'bar_model_part', 'fraction_equivalence_match', 'fraction_strip_builder', 'double_number_line', 'comma_shift', 'scientific_notation_builder', 'map_locate_region', 'map_locate_point', 'map_locate_line', 'map_place_cities'];
type RegistryStatus = 'registered' | 'allowlist' | 'unregistered';
function classifyType(type: QuestionType, allowlist: Set<QuestionType>): RegistryStatus {
  if (hasDataSchema(type)) return 'registered';
  if (allowlist.has(type)) return 'allowlist';
  return 'unregistered';
}
const STATUS_BADGE: Record<RegistryStatus, {
  label: string;
  cls: string;
  icon: React.ReactNode;
}> = {
  registered: {
    label: 'Validator',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: <ShieldCheck className="w-3 h-3" data-id="element-4857" data-mp="1nnootorofqgy" />
  },
  allowlist: {
    label: 'Allowlist',
    cls: 'bg-slate-50 text-slate-600 border-slate-200',
    icon: <FileJson className="w-3 h-3" data-id="element-4858" data-mp="1wn1yahfswf1o" />
  },
  unregistered: {
    label: 'Geen schema',
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: <AlertCircle className="w-3 h-3" data-id="element-4859" data-mp="21ng8o64vk12v" />
  }
};
export function SchemaEnforcementSection() {
  const [modes, setModes] = useState<Record<string, Mode>>({});
  const [originalModes, setOriginalModes] = useState<Record<string, Mode>>({});
  const [filter, setFilter] = useState('');
  const [saved, setSaved] = useState(false);
  const allowlist = useMemo(() => new Set<QuestionType>(getUnregisteredDataSchemaAllowlist()), []);
  useEffect(() => {
    let cancelled = false;
    loadGovernanceConfig().then(cfg => {
      if (cancelled) return;
      const next: Record<string, Mode> = {};
      ALL_QUESTION_TYPES.forEach(t => {
        next[t] = cfg.enforcement?.schemas?.[t] as Mode ?? 'enforce';
      });
      setModes(next);
      setOriginalModes(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const hasChanges = ALL_QUESTION_TYPES.some(t => modes[t] !== originalModes[t]);
  const handleSave = async () => {
    const cfg = await loadGovernanceConfig();
    const nextCfg: GovernanceConfigOverrides = {
      ...cfg,
      enforcement: {
        ...(cfg.enforcement ?? {}),
        schemas: {
          ...modes
        }
      }
    };
    const ok = await saveGovernanceConfig(nextCfg);
    if (ok) {
      setOriginalModes({
        ...modes
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };
  const handleReset = () => setModes({
    ...originalModes
  });
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return ALL_QUESTION_TYPES;
    return ALL_QUESTION_TYPES.filter(t => t.toLowerCase().includes(q));
  }, [filter]);
  // Group counts for the header summary.
  const stats = useMemo(() => {
    const counts = {
      registered: 0,
      allowlist: 0,
      unregistered: 0
    };
    ALL_QUESTION_TYPES.forEach(t => {
      counts[classifyType(t, allowlist)]++;
    });
    return counts;
  }, [allowlist]);
  return <div data-id="element-4860" data-mp="34u1yx3qx4vsi">
      <div className="flex items-start justify-between gap-3 mb-1" data-id="element-4861" data-mp="34ttmte2mduid">
        <div data-id="element-4862" data-mp="2qutwm1zgl7k3">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2" data-id="element-4863" data-mp="2qv2de4ypcx0k">
            <FileJson className="w-4 h-4 text-violet-500" data-id="element-4864" data-mp="2uwfigctz0w6f" />
            Schema Enforcement Ramp
            <Badge variant="info" size="sm" data-id="element-4865" data-mp="2gwmjju8aj9nd">
              FG4 · Fase C
            </Badge>
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-4866" data-mp="2lv7ilqbt3na8">
          <Button variant="ghost" size="sm" onClick={handleReset} disabled={!hasChanges} data-id="element-4867" data-mp="2lxtt9ytzuiwo">
            <RotateCcw className="w-3.5 h-3.5 mr-1" data-id="element-4868" data-mp="k3b9soh0wrj5" />
            Reset
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={!hasChanges} data-id="element-4869" data-mp="24llskfylwfkb">
            <Save className="w-3.5 h-3.5 mr-1" data-id="element-4870" data-mp="1zl7jcul9qzkp" />
            Opslaan
          </Button>
        </div>
        {saved && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded" data-id="element-4871" data-mp="29lhxqij1id1g">
            Opgeslagen
          </span>}
      </div>
      <p className="text-sm text-gray-500 mb-4" data-id="element-4872" data-mp="2neppca74f8j5">
        Bepaal per vraagtype hoe streng de data-shape schema's gehandhaafd
        worden. Volg de ramp{' '}
        <span className="font-mono text-[11px]" data-id="element-4873" data-mp="2x9re34xhi8l7">
          off → shadow → warn → enforce
        </span>
        . Unconfigured = <span className="font-mono text-[11px]" data-id="element-4874" data-mp="2scr62oh2qvrg">enforce</span>{' '}
        (structural default, voorkomt cliff-edge regressie).
      </p>

      {/* Registry signal summary */}
      <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-4875" data-mp="xz9snuadvfa5">
        <div className="px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50" data-id="element-4876" data-mp="c4snq8a660ww">
          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700" data-id="element-4877" data-mp="c2ye3q4clv84">
            Validator
          </p>
          <p className="text-lg font-bold text-emerald-800 leading-tight" data-id="element-4878" data-mp="lu2hdsknvrn4">
            {stats.registered}
          </p>
          <p className="text-[11px] text-emerald-700 opacity-80" data-id="element-4879" data-mp="qsnidu7qmfcm">
            Concrete data-shape check
          </p>
        </div>
        <div className="px-3 py-2 rounded-lg border border-slate-200 bg-slate-50" data-id="element-4880" data-mp="3pd5jrpmr0hle">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-600" data-id="element-4881" data-mp="3ub74sg3qiqzi">
            Allowlist
          </p>
          <p className="text-lg font-bold text-slate-800 leading-tight" data-id="element-4882" data-mp="367a8rjk1c2z">
            {stats.allowlist}
          </p>
          <p className="text-[11px] text-slate-600 opacity-80" data-id="element-4883" data-mp="1ej9rlotha5o7">
            Named exemption (S9)
          </p>
        </div>
        <div className="px-3 py-2 rounded-lg border border-amber-200 bg-amber-50" data-id="element-4884" data-mp="1ikbqg4jhgfpn">
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700" data-id="element-4885" data-mp="332npg0lablpj">
            Geen schema
          </p>
          <p className="text-lg font-bold text-amber-800 leading-tight" data-id="element-4886" data-mp="2z127i0tw8h8h">
            {stats.unregistered}
          </p>
          <p className="text-[11px] text-amber-700 opacity-80" data-id="element-4887" data-mp="2u9al82hrsxo5">
            Vereist registratie of allowlist
          </p>
        </div>
      </div>

      {/* Mode legend */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-id="element-4888" data-mp="2kco2pkcx2ipa">
        {MODES.map(m => {
        const meta = MODE_META[m];
        return <div key={m} className={`px-2.5 py-2 rounded-lg border text-[11px] ${meta.pill}`} data-id="element-4889" data-mp="2ffnxtcrszlzr">
              <p className="font-bold uppercase tracking-wider text-[10px]" data-id="element-4890" data-mp="26d6dj3vpl3fe">
                {meta.label}
              </p>
              <p className="opacity-80 leading-tight mt-0.5" data-id="element-4891" data-mp="9j1scjujf7mp">
                {meta.description}
              </p>
            </div>;
      })}
      </div>

      {/* Filter */}
      <div className="relative mb-3" data-id="element-4892" data-mp="24f7wx48x6gpr">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-4893" data-mp="24e6eufthktyj" />
        <Input type="search" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter vraagtypes (bijv. clock, map_, fraction)…" className="pl-9" data-id="element-4894" data-mp="1zgmynad8a73y" />
      </div>

      {/* Per-type controls */}
      <div className="space-y-1.5 max-h-[480px] overflow-y-auto pr-1" data-id="element-4895" data-mp="1kt1vjggcs7w1">
        {filtered.map(type => {
        const current = modes[type] ?? 'enforce';
        const original = originalModes[type] ?? 'enforce';
        const dirty = current !== original;
        const status = classifyType(type, allowlist);
        const badge = STATUS_BADGE[status];
        return <div key={type} className={`flex items-center gap-3 p-2.5 rounded-lg border ${dirty ? 'border-amber-200 bg-amber-50/40' : 'border-gray-100 bg-white'}`} data-id="element-4896" data-mp="120qyi38dr45u">
              <div className="min-w-0 flex-1" data-id="element-4897" data-mp="38oq8cphty37u">
                <p className="text-sm font-mono text-gray-800 truncate" data-id="element-4898" data-mp="38nonjtb0i2cx">
                  {type}
                </p>
                <span className={`inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border ${badge.cls}`} data-id="element-4899" data-mp="2zp329qe5o98g">
                  {badge.icon}
                  {badge.label}
                </span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0" data-id="element-4900" data-mp="3sbxnipisrcmz">
                {MODES.map(m => {
              const isActive = current === m;
              const meta = MODE_META[m];
              return <button key={m} type="button" onClick={() => setModes(prev => ({
                ...prev,
                [type]: m
              }))} className={`px-2 py-1 rounded text-[10px] font-medium border transition-colors ${isActive ? meta.pill : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`} data-id="element-4901" data-mp="3ne5vzwm2dsjb">
                      {meta.label}
                    </button>;
            })}
              </div>
            </div>;
      })}
        {filtered.length === 0 && <p className="text-xs text-gray-500 italic text-center py-6" data-id="element-4902" data-mp="197ftmo4pbu02">
            Geen vraagtypes komen overeen met "{filter}".
          </p>}
      </div>

      <p className="text-[11px] text-gray-500 mt-4 leading-relaxed" data-id="element-4903" data-mp="14a66jf4sbufe">
        💡 Tip: start altijd op <span className="font-mono" data-id="element-4904" data-mp="108caxl9g5gm3">shadow</span>,
        bekijk de Shadow Impact Report op het Overzicht tabblad, en ramp per
        vraagtype door naar <span className="font-mono" data-id="element-4905" data-mp="2ukiw9cw21ds4">warn</span> en{' '}
        <span className="font-mono" data-id="element-4906" data-mp="2uhwphfgzn2ay">enforce</span>. Types met label{' '}
        <span className="font-mono" data-id="element-4907" data-mp="2plpf9ahk74n7">Geen schema</span> moeten eerst een
        validator of allowlist-entry krijgen (zie{' '}
        <code className="text-[10px]" data-id="element-4908" data-mp="2x2unuungqkg7">
          src/content/questions/dataSchemas.ts
        </code>
        ).
      </p>
    </div>;
}